import { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { setParentalPin } from '../services/api';
import { toast } from 'sonner';
import { User, Shield, Bell, Monitor, Key, Save, Lock, Unlock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';

export default function SettingsPage() {
  const { user, refreshUser } = useAuth();
  const [parentalPin, setParentalPinState] = useState('');
  const [confirmPin, setConfirmPin] = useState('');
  const [parentalEnabled, setParentalEnabled] = useState(user?.parental_enabled || false);
  const [saving, setSaving] = useState(false);

  const handleSaveParental = async () => {
    if (parentalPin.length !== 4 || !/^\d{4}$/.test(parentalPin)) {
      toast.error('PIN must be 4 digits');
      return;
    }
    if (parentalPin !== confirmPin) {
      toast.error('PINs do not match');
      return;
    }

    setSaving(true);
    try {
      await setParentalPin(parentalPin, parentalEnabled);
      await refreshUser();
      toast.success('Parental controls updated');
      setParentalPinState('');
      setConfirmPin('');
    } catch (error) {
      toast.error('Failed to update parental controls');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto space-y-6" data-testid="settings-page">
      <div>
        <h1 className="text-3xl font-bold">Settings</h1>
        <p className="text-white/60 mt-1">Manage your account and preferences</p>
      </div>

      <Tabs defaultValue="account" className="space-y-6">
        <TabsList className="bg-[rgb(15,15,16)] border border-white/10">
          <TabsTrigger value="account" className="data-[state=active]:bg-[rgb(124,58,237)]">
            <User className="w-4 h-4 mr-2" />
            Account
          </TabsTrigger>
          <TabsTrigger value="parental" className="data-[state=active]:bg-[rgb(124,58,237)]">
            <Shield className="w-4 h-4 mr-2" />
            Parental Controls
          </TabsTrigger>
          <TabsTrigger value="playback" className="data-[state=active]:bg-[rgb(124,58,237)]">
            <Monitor className="w-4 h-4 mr-2" />
            Playback
          </TabsTrigger>
        </TabsList>

        {/* Account Tab */}
        <TabsContent value="account" className="space-y-6">
          <div className="glass-card rounded-xl p-6 space-y-6">
            <h3 className="text-lg font-semibold flex items-center gap-2">
              <User className="w-5 h-5 text-[rgb(124,58,237)]" />
              Account Information
            </h3>
            
            <div className="grid md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label className="text-white/60">Username</Label>
                <Input
                  value={user?.username || ''}
                  disabled
                  className="bg-[rgb(31,31,34)] border-white/10"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-white/60">Email</Label>
                <Input
                  value={user?.email || ''}
                  disabled
                  className="bg-[rgb(31,31,34)] border-white/10"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-white/60">Account Type</Label>
                <Input
                  value={user?.role === 'admin' ? 'Administrator' : 'User'}
                  disabled
                  className="bg-[rgb(31,31,34)] border-white/10"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-white/60">Member Since</Label>
                <Input
                  value={user?.created_at ? new Date(user.created_at).toLocaleDateString() : '-'}
                  disabled
                  className="bg-[rgb(31,31,34)] border-white/10"
                />
              </div>
            </div>
          </div>

          <div className="glass-card rounded-xl p-6 space-y-6">
            <h3 className="text-lg font-semibold flex items-center gap-2">
              <Key className="w-5 h-5 text-[rgb(16,185,129)]" />
              Subscription
            </h3>
            
            <div className="grid md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label className="text-white/60">Plan</Label>
                <Input
                  value={user?.subscription_type !== 'none' ? user?.subscription_type : 'No active plan'}
                  disabled
                  className="bg-[rgb(31,31,34)] border-white/10 capitalize"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-white/60">Credits</Label>
                <Input
                  value={`${user?.credits?.toFixed(2) || 0} credits`}
                  disabled
                  className="bg-[rgb(31,31,34)] border-white/10"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-white/60">Max Devices</Label>
                <Input
                  value={`${user?.max_devices || 1} device(s)`}
                  disabled
                  className="bg-[rgb(31,31,34)] border-white/10"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-white/60">Expires</Label>
                <Input
                  value={user?.subscription_expires ? new Date(user.subscription_expires).toLocaleDateString() : 'Never'}
                  disabled
                  className="bg-[rgb(31,31,34)] border-white/10"
                />
              </div>
            </div>
          </div>
        </TabsContent>

        {/* Parental Controls Tab */}
        <TabsContent value="parental" className="space-y-6">
          <div className="glass-card rounded-xl p-6 space-y-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-xl bg-[rgb(124,58,237)]/20 flex items-center justify-center">
                  <Shield className="w-6 h-6 text-[rgb(124,58,237)]" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold">Parental Controls</h3>
                  <p className="text-sm text-white/60">Restrict adult content with a PIN</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <span className="text-sm text-white/60">{parentalEnabled ? 'Enabled' : 'Disabled'}</span>
                <Switch
                  checked={parentalEnabled}
                  onCheckedChange={setParentalEnabled}
                  data-testid="parental-toggle"
                />
              </div>
            </div>

            {parentalEnabled && (
              <div className="border-t border-white/10 pt-6 space-y-4">
                <div className="flex items-center gap-2 text-sm text-white/60">
                  {user?.parental_pin ? (
                    <><Lock className="w-4 h-4" /> PIN is set</>
                  ) : (
                    <><Unlock className="w-4 h-4" /> No PIN set</>
                  )}
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>New PIN (4 digits)</Label>
                    <Input
                      type="password"
                      maxLength={4}
                      placeholder="Enter 4-digit PIN"
                      value={parentalPin}
                      onChange={(e) => setParentalPinState(e.target.value.replace(/\D/g, ''))}
                      className="bg-[rgb(31,31,34)] border-white/10"
                      data-testid="parental-pin-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Confirm PIN</Label>
                    <Input
                      type="password"
                      maxLength={4}
                      placeholder="Confirm PIN"
                      value={confirmPin}
                      onChange={(e) => setConfirmPin(e.target.value.replace(/\D/g, ''))}
                      className="bg-[rgb(31,31,34)] border-white/10"
                      data-testid="parental-confirm-pin-input"
                    />
                  </div>
                </div>

                <Button
                  onClick={handleSaveParental}
                  disabled={saving || !parentalPin || !confirmPin}
                  className="rounded-full bg-[rgb(124,58,237)]"
                  data-testid="save-parental-btn"
                >
                  <Save className="w-4 h-4 mr-2" />
                  {saving ? 'Saving...' : 'Save PIN'}
                </Button>
              </div>
            )}
          </div>
        </TabsContent>

        {/* Playback Tab */}
        <TabsContent value="playback" className="space-y-6">
          <div className="glass-card rounded-xl p-6 space-y-6">
            <h3 className="text-lg font-semibold flex items-center gap-2">
              <Monitor className="w-5 h-5 text-[rgb(59,130,246)]" />
              Playback Settings
            </h3>
            
            <div className="space-y-4">
              <div className="flex items-center justify-between p-4 rounded-lg bg-white/5">
                <div>
                  <p className="font-medium">Auto-play next channel</p>
                  <p className="text-sm text-white/40">Automatically play similar channels</p>
                </div>
                <Switch defaultChecked />
              </div>
              
              <div className="flex items-center justify-between p-4 rounded-lg bg-white/5">
                <div>
                  <p className="font-medium">HD Quality</p>
                  <p className="text-sm text-white/40">Stream in highest available quality</p>
                </div>
                <Switch defaultChecked />
              </div>
              
              <div className="flex items-center justify-between p-4 rounded-lg bg-white/5">
                <div>
                  <p className="font-medium">Low Latency Mode</p>
                  <p className="text-sm text-white/40">Reduce delay for live content</p>
                </div>
                <Switch defaultChecked />
              </div>
            </div>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
